"use client"

/* eslint-disable @next/next/no-img-element */
import { useState } from "react"
import { Badge, HoverCard, HoverCardContent, HoverCardTrigger } from "@ui/shad"
import { ChevronLeft, ChevronRight, Globe } from "lucide-react"

import type { Citation } from "./node"

interface CitationBadgeProps {
  citations: Citation[]
}

function getFaviconUrl(url: string): string {
  try {
    const domain = new URL(url).hostname
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
  } catch {
    return ""
  }
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "")
  } catch {
    return url
  }
}

export default function CitationBadgeComponent({ citations }: CitationBadgeProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  if (citations.length === 0) return null

  const currentCitation = citations[currentIndex]
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < citations.length - 1

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasPrev) setCurrentIndex(currentIndex - 1)
  }

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasNext) setCurrentIndex(currentIndex + 1)
  }

  return (
    <span className="inline">
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <a href={citations[0].url} target="_blank" rel="noopener noreferrer">
            <Badge
              variant="secondary"
              size="base"
              className="hover:bg-brand -translate-y-0.5 font-normal! transition-colors duration-200"
            >
              {getDomain(citations[0].url)}
              {citations.length > 1 && ` +${citations.length - 1}`}
            </Badge>
          </a>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-0" align="start">
          {citations.length > 1 && (
            <div className="border-b px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePrev}
                    disabled={!hasPrev}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <span className="text-muted-foreground w-6 text-center text-sm">
                    {currentIndex + 1}/{citations.length}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={!hasNext}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1">
                    {citations.slice(0, 3).map((citation, idx) => (
                      <img
                        key={idx}
                        src={getFaviconUrl(citation.url)}
                        alt=""
                        width={16}
                        height={16}
                        className="bg-background size-4 rounded-full border"
                        onError={(e) => {
                          e.currentTarget.style.display = "none"
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-muted-foreground text-xs">{citations.length} sources</span>
                </div>
              </div>
            </div>
          )}

          <a href={currentCitation.url} target="_blank" rel="noopener noreferrer" className="block p-3">
            <div className="flex items-center gap-1.5">
              <img
                src={getFaviconUrl(currentCitation.url)}
                alt=""
                width={16}
                height={16}
                className="size-4"
                onError={(e) => {
                  const parent = e.currentTarget.parentElement
                  if (parent) {
                    e.currentTarget.style.display = "none"
                    const globe = document.createElement("span")
                    globe.className = "size-4"
                    parent.prepend(globe)
                  }
                }}
              />
              <Globe className="hidden size-4" />
              <span className="text-muted-foreground text-sm">{getDomain(currentCitation.url)}</span>
            </div>
            <h4 className="hover:text-link-hover mt-1.5 text-sm leading-snug font-semibold">{currentCitation.title}</h4>
            {currentCitation.snippet && (
              <p className="text-muted-foreground mt-1 line-clamp-3 text-sm leading-snug">{currentCitation.snippet}</p>
            )}
          </a>
        </HoverCardContent>
      </HoverCard>
    </span>
  )
}
